import { LineupEntry } from "@aura-frontend/omnibus-az-client";

import { mapIPlayerToLineupEntry } from "@/pages/Tagging/utils/api-helpers";
import { GamePlayer } from "@/types/game";

type LineupSource = {
  currentLineup: GamePlayer[];
  currentPitcher?: GamePlayer | null;
  currentCatcher?: GamePlayer | null;
};

/**
 * Builds the LineupEntry[] payload for a team: pitcher first, then catcher,
 * then the batting order (1-indexed).
 */
export const buildLineupEntries = (team: LineupSource): LineupEntry[] => {
  const entries: LineupEntry[] = [];
  if (team.currentPitcher) {
    entries.push(mapIPlayerToLineupEntry(team.currentPitcher, "pitcher"));
  }
  if (team.currentCatcher) {
    entries.push(mapIPlayerToLineupEntry(team.currentCatcher, "catcher"));
  }
  team.currentLineup.forEach((player, i) => {
    entries.push(mapIPlayerToLineupEntry(player, i + 1));
  });
  return entries;
};

/** Who is where in a lineup, ignoring how each player's details are stored. */
const toLineupSlots = (entries: LineupEntry[]) =>
  entries
    .filter((entry) => entry.externalId)
    .map((entry) => `${entry.position}:${entry.externalId}`)
    .sort()
    .join("|");

/**
 * The lineup to save for one team when editing an event. Saving rebuilds a
 * lineup from the form, which can drop or reshape what was stored (players
 * without an id, unknown handedness, name format), so a lineup the user left
 * alone is sent back exactly as stored. It's only replaced when its players
 * or positions changed, or the team did.
 */
export const resolveLineupToSave = ({
  saved,
  current,
  isSameTeam,
}: {
  /** The team's lineup as stored, [] if none */
  saved: LineupEntry[];
  /** The form's lineup for the team, null if it didn't validate */
  current: LineupSource | null;
  /** Whether the team is still the one the stored lineup belongs to */
  isSameTeam: boolean;
}): LineupEntry[] => {
  if (!current) return isSameTeam ? saved : [];
  const built = buildLineupEntries(current);
  return isSameTeam && toLineupSlots(built) === toLineupSlots(saved)
    ? saved
    : built;
};
